import { GameTypeGamesCategoriesEnum } from './../../game-type-games/game-type-games.models';
import * as _ from 'lodash';
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { Observable, of } from "rxjs";
import { filter, switchMap, tap } from "rxjs/operators";
import { GetParamPlatformResponse, PlatformRequestTypes } from "../platform/platform.model";
import { PlatformService } from "../platform/platform.service";
import { Game, GamesState, GameWithScore, GameTypeGames } from "./games.model";
import { CoreService } from '../core.service';

@Injectable({
  providedIn: "root",
})
export class GamesStore extends ComponentStore<GamesState> {
  private readonly gameTypeGamesEnum: typeof GameTypeGamesCategoriesEnum = GameTypeGamesCategoriesEnum;
  private recentGamesPlatformDataKey = 'recentgames';

  constructor(private http: HttpClient, private platformService: PlatformService, private coreService: CoreService) {
    super({ allGames: [] });
  }

  /*
   * Selectors read state out of our component store
   */
  readonly allGames$: Observable<Game[]> = this.select(
    (state) => state.allGames
  );
  readonly filteredGames$: Observable<Game[]> = this.select(
    (state) => state.filteredGames
  );
  readonly gameExlusions$: Observable<Game[]> = this.select(
    (state) => state.exclusionList
  );
  readonly gamesWithoutExclusions$ = this.select(
    this.allGames$,
    this.gameExlusions$,
    (allGames, exclusions) => {
      if (allGames && exclusions) {
        return allGames.filter((game) => !exclusions.includes(game))
      }
    }
  );

  readonly gameTypeGames$ = this.select(
    (state) => state.gameTypeGames
  );
  readonly providerGames$ = this.select(
    (state) => state.providerGames
  );
  readonly gameDetails$: Observable<Game[]> = this.select(
    (state) => state.gameDetails
  );
  readonly becauseYouPlayedFromCurrentGame$: Observable<Game[]> = this.select(
    (state) => state.becauseYouPlayedFromCurrentGame
  );
  readonly providerGamesFromCurrentGame$: Observable<Game[]> = this.select(
    (state) => state.providerGamesFromCurrentGame
  );
  readonly groupedByCategoryGames$: Observable<GameTypeGames> = this.select(
    (state) => state.groupedGameTypeGames
  );
  readonly gamesByProvider$: Observable<Game[]> = this.select(
    (state) => state.gamesByProvider
  );
  readonly newToSkolGames$: Observable<Game[]> = this.select(
    (state) => state.newToSkolGames
  );
  readonly popularGames$: Observable<Game[]> = this.select(
    (state) => state.popularGames
  );
  readonly numberOfGamesLessThanTen$: Observable<boolean> = this.select(
    (state) => state.numberOfGamesLessThanTen
  );

  getGameByLaunchCode(launchCode: string): Observable<Game> {
    return this.select((state) => state.allGames.find(game => game.launchcode === launchCode));
  }

  getGameBySeoName(seoName: string): Observable<Game> {
    return this.select((state) => state.allGames.find(game => game.seo_name === seoName));
  }

  getAmountOfGamesByProvider(providerName: string): Observable<number> {
    return this.select((state) => state.allGames.filter(game => {
      return game.provider === providerName
    }).length);
  }

  /*
   * Updaters act as reducers as such and add values to our component state
   */
  readonly addGames = this.updater((state, games: Game[]) => ({
    ...state, allGames: games
  }));
  readonly addFilteredGames = this.updater((state, games: Game[]) => ({
    ...state, filteredGames: games
  }));
  readonly addExclusionList = this.updater((state, games: Game[]) => ({
    ...state, exclusionList: games
  }));
  readonly addGameTypeGames = this.updater((state, games: Game[]) => ({
    ...state, gameTypeGames: games
  }));
  readonly addProviderGames = this.updater((state, games: Game[]) => ({
    ...state, providerGames: games
  }));
  readonly gameDetails = this.updater((state, launchCode: string) => ({
    ...state, gameDetails: [...state.allGames.filter(el => el.launchcode === launchCode)]
  }));
  readonly addBecauseYouPlayedFromCurrentGame = this.updater((state, games: Game[]) => ({
    ...state, becauseYouPlayedFromCurrentGame: games
  }));
  readonly addProviderGamesFromCurrentGame = this.updater((state, games: Game[]) => ({
    ...state, providerGamesFromCurrentGame: games
  }));
  readonly addGroupedByCategoryGames = this.updater((state, groupedGameTypeGames: GameTypeGames) => ({
    ...state, groupedGameTypeGames: groupedGameTypeGames
  }));
  readonly addGamesByProvider = this.updater((state, games: Game[]) => ({
    ...state, gamesByProvider: games
  }));
  readonly addNewToSkolGames = this.updater((state, games: Game[]) => ({
    ...state, newToSkolGames: games
  }));
  readonly addPopularGames = this.updater((state, games: Game[]) => ({
    ...state, popularGames: games
  }));
  readonly addNumberOfGamesLessThanTen = this.updater((state, isLessThanTen: boolean) => ({
    ...state, numberOfGamesLessThanTen: isLessThanTen
  }));

  /*
   * Effects trigger side effects which manipulate the components state (we can also do this by calling .setState() in our component)
   * Effects are automatically subscribed to in the component they are called in for the life of the component
   */

  /*
  *  New to skol games are retrieved from the category property from the cms (gamesJSON.php),
  *  Content managers can change content from the CMS like this, we can also call the cms from a middleware
  */
  readonly getNewToSkolGames = this.effect(() => {
    return this.allGames$.pipe(
      switchMap(games => {
        const newGames: Game[] = [];
        games.forEach(game => {
          let isNewCategory: boolean = false;
          game.categories.forEach(category => {
            if (category.category === 'new' || category.category === 'mobilenew') {
              isNewCategory = true
            }
          });
          if (isNewCategory) {
            newGames.push(game);
          }
        });
        const randomizedNewGames = _.shuffle(newGames);
        return of(randomizedNewGames);
      }),
    ).pipe(tap((newGames: Game[]) => this.addNewToSkolGames(newGames)))
  })

  readonly getProviderGames = this.effect((providerName$: Observable<string>) => {
    return providerName$.pipe(
      switchMap(providerName => {
        return this.allGames$.pipe(
          switchMap(games => {
            const providerGames: Game[] = [];
            games.map(game => {
              if (game.provider.toLowerCase() === providerName.toLowerCase()) {
                providerGames.push(game);
              };
            });
            return of(providerGames);
          })
        ).pipe(tap(games => this.addGamesByProvider(games)))
      })
    )
  });

  readonly getGames = this.effect(() => {
    return this.http
      .get<Game[]>(this.coreService.legacyUrl + `/ajax/gamesJSON.php`)
      .pipe(
        tap((games: Game[]) => this.addGames(games)),
        tap(() => this.generateExclusionList()),
        tap(() => this.groupGameTypeGames())
      );
  });

  public generateExclusionList = this.effect(() => {
    return this.allGames$.pipe(
      filter(x => !!x && x.length > 0),
      switchMap(allGames => {
        const exclusionList: Game[] = [];
        allGames.forEach(game => {
          // @TODO - map volatility to enum
          if (game.volatility === "HIGH" || game.rtp >= 97) {
            exclusionList.push(game)
          }
        });
        return of(exclusionList);
      }),
      tap((exclusionList: Game[]) => this.addExclusionList(exclusionList)));
  });

  public groupGameTypeGames = this.effect(() => {
    return this.allGames$.pipe(
      filter(x => !!x && x.length > 0),
      switchMap(allGames => {
        // if we dynamically define game types then allGames needs to be looped first and populate the upcoming array of game types
        const byDefaultGameTypes = Object.keys(this.gameTypeGamesEnum);
        let groupedByCategoryGames = byDefaultGameTypes.reduce((a, b) => (a[b] = {
          popular: [],
          ascending: [],
          descending: []
        }, a), {});

        // This logic may change, currently we group game type games by categories values
        allGames.forEach(game => {
          game.categories.push({ 'category': 'all' }); // all games must contain all category
          let filteredCategoryArray = _.cloneDeep(game.categories);
          let gameCategories = _.uniqBy(filteredCategoryArray, 'category'); // exclude repeated categories
          gameCategories.forEach(category => {
            for (const key in groupedByCategoryGames) {
              if (key === category.category) {
                groupedByCategoryGames[key]['popular'].push(game);
                groupedByCategoryGames[key]['ascending'].push(game);
                groupedByCategoryGames[key]['descending'].push(game);
              }
            }
          });
        });
        // Sorting categories ascending and descending
        for (const key in groupedByCategoryGames) {
          groupedByCategoryGames[key]['ascending'] = _.sortBy(groupedByCategoryGames[key]['ascending'], (game) => {
            return isNaN(game.name.charAt(0)) ? game.name : null; // put games starting with a number at the end of the array
          })
          groupedByCategoryGames[key]['descending'].sort((a, b) => b.name.localeCompare(a.name, undefined, { numeric: true, sensitivity: 'base' }));
        }
        return of(groupedByCategoryGames);
      }),
      tap((groupedByCategoryGames: GameTypeGames) => this.addGroupedByCategoryGames(groupedByCategoryGames)));
  });


  /*
  * We can assume the first game has the highest score thanks to sorting,
  * We can also assume that more than 10 games will have this score since we are scoring based on provider and gametype
  */
  private randomizeHighestScoringGames(gamesWithScore: GameWithScore[]): Observable<Game[]> {
    const highestScore: number = gamesWithScore[0].totalScore;
    const gamesWithHighScore = gamesWithScore.filter(game => {
      return game.totalScore === highestScore;
    });
    const randomizedGames = _.shuffle(gamesWithHighScore).map(game => game.game);
    return of(randomizedGames);
  }

  /*
 * This will generate a list of games based on the players most frequently played provider
 *
 * @TODO - this logic should be moved to BE/Middleware and should not be on the FE!
 */
  public generateProviderGames = this.effect(() => {
    return this.allGames$.pipe(
      switchMap(recentGames => {
        return this.gamesWithoutExclusions$.pipe(
          filter(x => !!x && x.length > 0),
          switchMap(validGames => {

            if (recentGames && recentGames.length > 0) {
              const recentGamesFromValidGames: Game[] = validGames.filter(game => {
                return recentGames.forEach(allGamesGame => {
                  return allGamesGame.launchcode.includes(game.launchcode);
                })
              });

              /*
            * We then generate a score depending on provider matches
            */
              const providerCount: Record<string, number> = {};
              recentGamesFromValidGames.forEach(value => {
                providerCount[value.provider] = (providerCount[value.provider] || 0) + 1;
              });
              return this.getRandomHighestScoringGames(validGames, { provider: providerCount });
            } else {
              /*
               * If the user has no gameplay history,
               * Get most popular studio (we do not have the most popular studio currently, taking NetEnt as default)
               */
              return this.getRandomHighestScoringGames(validGames, { provider: { 'NetEnt': 1 } });
            }
          })
        ).pipe(tap(providerGames => this.addProviderGames(providerGames)))
      })
    )
  });

  /*
  * @TODO - this logic should be moved to BE/Middleware and should not be on the FE!
  */
  public generateBecauseYouPlayedGamesFromCurrentGame = this.effect((currentGame$: Observable<Game>) => {
    return currentGame$.pipe(
      switchMap(currentGame => {
        return this.gamesWithoutExclusions$.pipe(
          filter(x => !!x && x.length > 0),
          switchMap(validGames => {
            const score = { provider: {}, gameType: {} };
            score.provider[currentGame.provider] = 1;
            score.gameType[currentGame.type] = 1;

            return this.getRandomHighestScoringGames(validGames, score);
          })
        ).pipe(tap(becauseYouPlayedGames => this.addBecauseYouPlayedFromCurrentGame(becauseYouPlayedGames)))
      })
    )
  });

  /*
  * @TODO - this logic should be moved to BE/Middleware and should not be on the FE!
  */
  public generateProviderGamesFromCurrentGame = this.effect((currentGame$: Observable<Game>) => {
    return currentGame$.pipe(
      switchMap(currentGame => {
        return this.allGames$.pipe(
          filter(x => !!x && x.length > 0),
          switchMap(validGames => {
            const validGamesWithProvider: Game[] = validGames.filter(game => {
              return currentGame.launchcode === game.launchcode
            });
            /*
          * We then generate a score depending on provider matches
          */
            const providerCount: Record<string, number> = {};
            validGamesWithProvider.forEach(value => {
              providerCount[value.provider] = (providerCount[value.provider] || 0) + 1;
            });
            return this.getRandomHighestScoringGames(validGames, { provider: providerCount });
          })
        ).pipe(tap(providerGames => this.addProviderGamesFromCurrentGame(providerGames)))
      })
    )
  });

  public getPopularGames = this.effect((country$: Observable<string>) => {
    return country$.pipe(
      switchMap(countryCode => {
        return this.allGames$.pipe(
          filter(x => !!x),
          switchMap(allGames => {
            return this.platformService.execute(PlatformRequestTypes.GET_GAME_POPULARITY, {
              // brand: "118", // @todo -> currently no data for skol, pass in future
              allBrands: true,
              country: countryCode
            }).pipe(
              switchMap(popularGamesResponse => {
                const popularGames: Game[] = [];
                allGames.forEach(game => {
                  if (_.includes(popularGamesResponse.games, game.launchcode)) {
                    popularGames.push(game);
                  }
                });
                return of(popularGames);
              })
            )
          })
        ).pipe(tap(games => this.addPopularGames(games)));
      })
    )
  });

  public getGameDetails(launchCode: string): Observable<Game[]> {
    this.gameDetails(launchCode);
    return this.gameDetails$;
  }

  private getRandomHighestScoringGames(
    games: Game[],
    { provider, gameType }: Partial<Record<'gameType' | 'provider', Record<string, number>>>
  ) {
    /*
     * We generate a score for each game depending on what provider and gametype the game has,
     * Also sort them
     */
    const sortedGames: GameWithScore[] = games
      .map((game) => {
        const scoreProvider = (provider && provider[game.provider]) || 0;
        const scoreGameType = (gameType && gameType[game.type]) || 0;
        const totalScore = scoreProvider + scoreGameType;

        return {
          game,
          totalScore,
        };
      })
      .sort((prevGame, nextGame) => nextGame.totalScore - prevGame.totalScore);

    return this.randomizeHighestScoringGames(sortedGames);
  }
}
